import React, { useEffect, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { Col, Row } from 'reactstrap';
import { useFetchWrapper } from '../../../hooks/useFetchWrapper';
import Helper from '../../../utilities/helper';
import { PermissionsGate } from '../../../utilities/PermissionsGate';
import { ACCESS_VALUES, PageNames } from '../../../utilities/constValues';
import { PermissionsRequired } from '../../Shared/PermissionsRequired';
import { FundingSources } from './FundingSources';
import { GreenHouseGas } from './GreenHouseGas';
import { RecommendedEquipment } from './RecommendedEquipment';
import { SustainedImpact } from './SustainedImpact';
import { TopLevelResults } from './TopLevelResults';
import { ContentBlock } from '../../Shared/Content/ContentBlock';
import { ContentBody } from '../../Shared/Content/ContentBody';
import { clientConfig } from '../../../utilities/configService';

interface ITopLevelProps {
    fleetID: string,
    tco: number,
    annualFuelSavings: number,
    annualOandMSavings: number,
    cO2Reductions: number
}

interface IRealWorldEquivalents {
    homeEnergyUse: string,
    incandescentSwap: string,
    wasteToLandfill: string,
    treesPlanted: string
}

interface IVehicleRecommendations {
    vehicleCategoryName: string,
    replacementVehicleType: string,
    replacementVehicleCount: number,
    avgVehicleRange: number,
    avgBatteryCapacity: number,
    imagePath: string
    imageID: number
}

interface IChargerRecommendations {
    replacementChargerType: string,
    replacementChargerCount: string,
    chargerTypeNameLong: string,
    description: string,
    chargeLoadRange: string,
    chargeTimeRange: string,
    input: string,
    imagePath: string
}

interface IAnnualEmissions {
    currentFleetEstimatedAnnualEmissions: number,
    replacementFleetEstimatedAnnualEmissions: number
}

interface IFleetSummary {
    fleetName: string,
    fleetID: string,
    isSample: boolean,
    isDefault: boolean,
    totalVehicleCount: number,
    vehicleTypes: IVehicleSet[]
}

interface IVehicleSet {
    vehicleTypeName: string,
    vehicleCount: number,
    imagePath: string
}

export const PrintEstimate = () => {
    const fetchWrapper = useFetchWrapper();
    const applicationName = clientConfig?.APPLICATION_NAME || '';

    const location = useLocation();
    const [fleetID, setFleetID] = useState('');
    const [fleetSummary, setFleetSummary] = useState<IFleetSummary>();
    const [topLevelResults, setTopLevelResults] = useState<ITopLevelProps>();
    const [realWorldEquivResults, setRealWorldEquivResults] = useState<IRealWorldEquivalents>();
    const [annualEmissionsResults, setAnnualEmissionsResults] = useState<IAnnualEmissions>();
    const [vehicleRecommendations, setVehicleRecommendations] = useState<IVehicleRecommendations[]>();
    const [chargerRecommendations, setChargerRecommendations] = useState<IChargerRecommendations[]>();

    React.useEffect(() => {
        Helper.setPagePath(location.pathname);
    }, [location]);

    const { fleetIDParam } = useParams();
    if (!fleetID) {
        if (fleetIDParam) {
            setFleetID(fleetIDParam);
        }
    };

    useEffect(() => {
        getFleetByID(fleetID);
        getTopLevelResults();
        getRealWorldEquivalents();
        getAnnualEmissions();
        getVehicleRecommendations();
        getChargerRecommendations();
    }, [fleetID]);

    const getFleetByID = (summaryFleetID: any) => {
        fetchWrapper.get(`fleet/fleetRollup/${summaryFleetID}/2`)
            .then((data) => {
                if (data.fleetRollup) {
                    setFleetSummary(data.fleetRollup);
                }
            });
    };

    const getTopLevelResults = () => {
        fetchWrapper.get(`fleetestimator/toplevelcalcs/${applicationName}/${fleetID}`)
            .then((data) => {
                if (data.topLevelCalcs) {
                    setTopLevelResults(data.topLevelCalcs);
                }
            });
    };

    const getRealWorldEquivalents = () => {
        fetchWrapper.get(`fleetestimator/realworldequivalents/${fleetID}/0`)
            .then((data) => {
                if (data.realWorldEquivalents) {
                    setRealWorldEquivResults(data.realWorldEquivalents);
                }
            });
    };

    const getAnnualEmissions = () => {
        fetchWrapper.get(`fleetestimator/annualemission/${fleetID}/0`)
            .then((data) => {
                if (data.annualEmission) {
                    setAnnualEmissionsResults(data.annualEmission);
                }
            });
    };

    const getVehicleRecommendations = () => {
        fetchWrapper.get(`fleetestimator/vehiclerecommendation/${fleetID}/0`)
            .then((data) => {
                if (data.vehicleRecommendations) {
                    setVehicleRecommendations(data.vehicleRecommendations);
                }
            });
    };

    const getChargerRecommendations = () => {
        fetchWrapper.get(`fleetestimator/chargerrecommendations/${fleetID}/0`)
            .then((data) => {
                if (data.chargerRecommendations) {
                    setChargerRecommendations(data.chargerRecommendations);
                }
            });
    };


    window.addEventListener("beforeprint", (event) => {
        document.title = `Results for ${fleetSummary?.fleetName}`;
    });

    return (
        <PermissionsGate requestedPermission={ACCESS_VALUES.Resources} renderError={<PermissionsRequired showParticipantMsg={true} />} pageName={PageNames.PrintEstimate}>
        <div className="viewEstimate printEstimate">
            <Row className="contentPageHeading printHeading">
                <div className="titleBlock">
                    <div className="fleetSummary">
                        <h1 className="title printPageHead css_heading bold" id="printPageHead">Results for {fleetSummary?.fleetName}</h1>
                    </div>
                </div>
            </Row>
            <ContentBlock className="printPageBody">
                <ContentBody>

                    <Col>
                        <Row className="componentPadding page-break-inside-avoid">
                            <Col className="sectionTitle">
                                <h2 className="css_heading bold">Principal Results</h2>
                            </Col>
                        </Row>
                        <Row className="sectionPadding page-break-inside-avoid">
                            <Col>
                                {topLevelResults ? (
                                    <TopLevelResults topLevelData={topLevelResults} />
                                ) : ''}
                            </Col>
                        </Row>
                        <Row className="sectionPadding page-break-inside-avoid">
                            <Col>
                                {realWorldEquivResults && annualEmissionsResults ? (
                                    <GreenHouseGas realWorldData={realWorldEquivResults} emissionsData={annualEmissionsResults} isPrintView={true} />
                                ) : ''}
                            </Col>
                        </Row>
                        <Row className="sectionPadding">
                            <Col>
                                {chargerRecommendations && vehicleRecommendations ? (
                                    <RecommendedEquipment chargerData={chargerRecommendations} vehicleData={vehicleRecommendations} isPrintView={true} />
                                ) : ''}
                            </Col>
                        </Row>
                    </Col>
                </ContentBody>
                <Row className="page-break-before-always" id="SustainedImpact">
                    <Col className="page-break-inside-avoid">
                        <Row className="">
                            <Col className="sectionTitle">
                                <h2 className="css_heading bold">Sustained Impact</h2>
                            </Col>
                        </Row>
                        <Row className="sectionPadding page-break-inside-avoid">
                            <Col>
                                <div id="SustainedImpact" className="sustainedImpactContainer">
                                    <SustainedImpact fleetID={fleetID} />
                                </div>
                            </Col>
                        </Row>
                    </Col>
                </Row>
                <Row className="page-break-before-always" id="Funding">
                    <Col className="page-break-inside-avoid">
                        <Row>
                            <Col>
                                <FundingSources fleetID={fleetID} />
                            </Col>
                        </Row>
                    </Col>
                </Row>
            </ContentBlock >
        </div>
        </PermissionsGate>
    );
}
